import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Edit2,
  Check,
  Loader2,
  ZoomIn,
  ZoomOut,
  Maximize,
  Move,
  MousePointer2,
  Grid3X3,
  Magnet,
  Copy,
  Eye,
  EyeOff,
  Layers,
  MoveUpRight,
  Cuboid,
  Scan,
  ScanLine,
  RotateCcw,
  X,
} from 'lucide-react';
import axios from 'axios';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { API } from '../config';

const MIN_ROOM_SIZE = 30;
const MIN_ZOOM = 0.35;
const MAX_ZOOM = 6;

const FloorPlanEditor = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const canvasRef = useRef(null);
  const canvasWrapperRef = useRef(null);
  const userAdjustedViewRef = useRef(false);

  const [image, setImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [carpetPolygons, setCarpetPolygons] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingSize, setEditingSize] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [show3DPreview, setShow3DPreview] = useState(false);
  const [focusMode, setFocusMode] = useState(false);

  const [tool, setTool] = useState('select');
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [gridSize, setGridSize] = useState(40);

  const [canvasSize, setCanvasSize] = useState({ width: 1200, height: 760 });
  const [fitScale, setFitScale] = useState(1);
  const [viewport, setViewport] = useState({ zoom: 1, panX: 0, panY: 0 });

  const [dragging, setDragging] = useState(null);
  const [compass, setCompass] = useState({ x: 100, y: 100, rotation: 0, size: 80 });
  const [compassRotating, setCompassRotating] = useState(false);
  const [showCompass, setShowCompass] = useState(true);

  const {
    floorPlanBase64,
    propertyType,
    orientation,
    detectedRooms = [],
    detectionMeta = {},
  } = location.state || window.mobileInitialState || {};

  const [detectionStatus, setDetectionStatus] = useState(
    detectionMeta?.detection_status || (Array.isArray(detectedRooms) && detectedRooms.length > 0 ? 'success' : 'unknown')
  );
  const [detectionErrors, setDetectionErrors] = useState(
    Array.isArray(detectionMeta?.errors) ? detectionMeta.errors : []
  );
  const [detectionMetaState, setDetectionMetaState] = useState(detectionMeta || {});
  const [retryingDetection, setRetryingDetection] = useState(false);
  const detectionFailed = detectionStatus === 'failed';

  const roomTypes = [
    'Living Room',
    'Living Cum Dining',
    'Kitchen',
    'Kitchen / Dining',
    'Bedroom',
    'Master Bedroom',
    'Bathroom',
    'Toilet',
    'Powder Toilet',
    'Hall / Dining',
    'Dining Room',
    'Den',
    'Den Room',
    'Ensuite',
    'Laundry',
    'Utility',
    'Lift',
    'Stairs',
    'Terrace',
    'Entry',
    'Study Room',
    'Pooja Room',
    'Entrance',
    'Balcony',
    'Store Room',
    'Servant Room',
  ];

  const safeNum = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  };

  const normalizeDetectedRoomType = (type) => {
    const value = String(type || '').trim();
    if (!value || value.toLowerCase() === 'unknown' || value.toLowerCase() === 'unknown room') {
      return 'Room';
    }
    return value;
  };

  const cleanupDetectedRoomSemantics = (detectedRooms, targetImage) => {
    if (!Array.isArray(detectedRooms) || detectedRooms.length === 0 || !targetImage) {
      return detectedRooms || [];
    }

    const livingRooms = detectedRooms.filter((room) => String(room.type || '').toLowerCase().includes('living'));
    if (livingRooms.length <= 1) return detectedRooms;

    const scoredLiving = livingRooms
      .map((room) => {
        const centerY = room.y + room.height / 2;
        const centerX = room.x + room.width / 2;
        const area = room.width * room.height;
        const lowerPublicScore = centerY / Math.max(targetImage.height, 1);
        const centralScore = 1 - Math.min(1, Math.abs(centerX - targetImage.width / 2) / Math.max(targetImage.width / 2, 1));
        return {
          room,
          score: lowerPublicScore * 0.62 + centralScore * 0.16 + Math.min(1, area / (targetImage.width * targetImage.height * 0.15)) * 0.22,
        };
      })
      .sort((a, b) => b.score - a.score);

    const keepLivingId = scoredLiving[0]?.room?.id;
    const privateCutoff = targetImage.height * 0.48;

    return detectedRooms.map((room) => {
      if (!String(room.type || '').toLowerCase().includes('living') || room.id === keepLivingId) {
        return room;
      }

      const centerY = room.y + room.height / 2;
      const replacementType = centerY < privateCutoff ? 'Bedroom' : 'Hall / Dining';
      return {
        ...room,
        type: replacementType,
        semantic_cleanup: 'duplicate_living_room',
      };
    });
  };

  const toCanvasDetections = useCallback(
    (detected, meta, targetImage) => {
      const rawConvertedRooms = Array.isArray(detected)
        ? detected
            .map((room, index) => {
              const x = (safeNum(room.x) / 100) * targetImage.width;
              const y = (safeNum(room.y) / 100) * targetImage.height;
              const width = (safeNum(room.width) / 100) * targetImage.width;
              const height = (safeNum(room.height) / 100) * targetImage.height;
              const polygon = Array.isArray(room.polygon)
                ? room.polygon
                    .map((point) => ({
                      x: (safeNum(point.x) / 100) * targetImage.width,
                      y: (safeNum(point.y) / 100) * targetImage.height,
                    }))
                    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
                : undefined;

              return {
                id: room.id ?? Date.now() + index,
                type: normalizeDetectedRoomType(room.type),
                confidence: safeNum(room.confidence),
                vastu_zone: room.vastu_zone || 'CENTER',
                x,
                y,
                width,
                height,
                polygon: polygon && polygon.length >= 3 ? polygon : undefined,
              };
            })
            .filter((room) => room.width > 0 && room.height > 0)
        : [];

      const provider = String(meta?.provider || meta?.model_provider || '').toLowerCase();
      const hasCuratedOrTextLabels =
        provider.includes('curated') ||
        detected.some((room) => String(room.detected_label || '').trim().length > 0);
      const convertedRooms = hasCuratedOrTextLabels
        ? rawConvertedRooms
        : cleanupDetectedRoomSemantics(rawConvertedRooms, targetImage);

      const convertedCarpet = Array.isArray(meta?.carpet_polygons)
        ? meta.carpet_polygons
            .map((poly) =>
              Array.isArray(poly)
                ? poly
                    .map((point) => ({
                      x: (safeNum(point.x) / 100) * targetImage.width,
                      y: (safeNum(point.y) / 100) * targetImage.height,
                    }))
                    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
                : []
            )
            .filter((poly) => poly.length >= 3)
        : [];

      return { convertedRooms, convertedCarpet };
    },
    []
  );

  const getRoomColor = useCallback(
    (roomId, alpha = 1, lightness = 48) => {
      const room = rooms.find((r) => r.id === roomId);
      let hue = 215; // default Blue/Slate-ish
      if (room && room.type) {
        const type = room.type.toLowerCase().trim();
        if (type.includes('living') || type.includes('hall') || type.includes('dining') || type.includes('drawing')) {
          hue = 270; // Purple
        } else if (type.includes('kitchen')) {
          hue = 32;  // Orange
        } else if (type.includes('bedroom')) {
          hue = 210; // Blue
        } else if (type.includes('bath') || type.includes('toilet') || type.includes('wash') || type.includes('wc') || type.includes('powder')) {
          hue = 330; // Pink
        } else if (type.includes('pooja') || type.includes('puja') || type.includes('temple') || type.includes('prayer')) {
          hue = 45;  // Gold/Yellow
        } else if (type.includes('stair')) {
          hue = 120; // Green
        } else if (type.includes('balcony') || type.includes('utility') || type.includes('verandah')) {
          hue = 160; // Teal
        } else if (type.includes('corridor') || type.includes('passage') || type.includes('lobby') || type.includes('foyer')) {
          hue = 200; // Sky Blue
        } else {
          // generic fallback or hash-based hue to avoid visual clash
          const idStr = String(roomId);
          let hash = 0;
          for (let i = 0; i < idStr.length; i++) {
            hash = (hash * 31 + idStr.charCodeAt(i)) % 360;
          }
          hue = hash;
        }
      } else {
        const idStr = String(roomId);
        let hash = 0;
        for (let i = 0; i < idStr.length; i++) {
          hash = (hash * 31 + idStr.charCodeAt(i)) % 360;
        }
        hue = hash;
      }
      return `hsla(${hue}, 82%, ${lightness}%, ${alpha})`;
    },
    [rooms]
  );

  const effectiveScale = useMemo(() => fitScale * viewport.zoom, [fitScale, viewport.zoom]);

  const snapValue = useCallback(
    (value) => {
      if (!snapToGrid || gridSize <= 1) return value;
      return Math.round(value / gridSize) * gridSize;
    },
    [gridSize, snapToGrid]
  );

  const clampRoom = useCallback(
    (room) => {
      if (!image) return room;
      const maxX = Math.max(0, image.width - room.width);
      const maxY = Math.max(0, image.height - room.height);
      return {
        ...room,
        x: Math.max(0, Math.min(maxX, room.x)),
        y: Math.max(0, Math.min(maxY, room.y)),
      };
    },
    [image]
  );

  const getPolygonCentroid = (polygon, room) => {
    if (!Array.isArray(polygon) || polygon.length < 3) {
      return { x: room.x + room.width / 2, y: room.y + room.height / 2 };
    }
    const total = polygon.reduce((acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }), {
      x: 0,
      y: 0,
    });
    return { x: total.x / polygon.length, y: total.y / polygon.length };
  };

  const getRoomDisplayLabel = (room) => {
    const type = normalizeDetectedRoomType(room.type);
    return type === 'Room' ? `Room ${room.id}` : type;
  };

  const getRoomBounds = (room) => {
    if (Array.isArray(room.polygon) && room.polygon.length >= 3) {
      const xs = room.polygon.map((p) => p.x);
      const ys = room.polygon.map((p) => p.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      const maxX = Math.max(...xs);
      const maxY = Math.max(...ys);
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
    return room;
  };

  const pointInPolygon = (point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;
      const intersect =
        yi > point.y !== yj > point.y &&
        point.x < ((xj - xi) * (point.y - yi)) / (yj - yi + 0.00001) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const fitToView = useCallback(() => {
    if (!image || !canvasSize.width || !canvasSize.height) return;
    const nextFitScale = Math.min(canvasSize.width / image.width, canvasSize.height / image.height);
    const drawnWidth = image.width * nextFitScale;
    const drawnHeight = image.height * nextFitScale;
    setFitScale(nextFitScale);
    setViewport({
      zoom: 1,
      panX: (canvasSize.width - drawnWidth) / 2,
      panY: (canvasSize.height - drawnHeight) / 2,
    });
    userAdjustedViewRef.current = false;
  }, [canvasSize.height, canvasSize.width, image]);

  const getScreenPoint = useCallback((event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }, []);

  const getWorldPoint = useCallback(
    (event) => {
      const screen = getScreenPoint(event);
      return {
        x: (screen.x - viewport.panX) / (effectiveScale || 1),
        y: (screen.y - viewport.panY) / (effectiveScale || 1),
        screenX: screen.x,
        screenY: screen.y,
      };
    },
    [effectiveScale, getScreenPoint, viewport.panX, viewport.panY]
  );

  const zoomAtPoint = useCallback(
    (screenX, screenY, nextZoom) => {
      const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nextZoom));
      const worldX = (screenX - viewport.panX) / (effectiveScale || 1);
      const worldY = (screenY - viewport.panY) / (effectiveScale || 1);
      const nextScale = fitScale * clampedZoom;
      setViewport({
        zoom: clampedZoom,
        panX: screenX - worldX * nextScale,
        panY: screenY - worldY * nextScale,
      });
      userAdjustedViewRef.current = true;
    },
    [effectiveScale, fitScale, viewport.panX, viewport.panY]
  );

  const drawGrid = (ctx) => {
    if (!showGrid || !image) return;
    const spacing = gridSize;
    const thinLine = Math.max(1 / effectiveScale, 0.5);
    ctx.strokeStyle = 'rgba(80, 80, 80, 0.18)';
    ctx.lineWidth = thinLine;

    for (let x = 0; x <= image.width; x += spacing) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, image.height);
      ctx.stroke();
    }
    for (let y = 0; y <= image.height; y += spacing) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(image.width, y);
      ctx.stroke();
    }
  };

  const drawCompass = (ctx) => {
    const size = compass.size;
    ctx.save();
    ctx.translate(compass.x, compass.y);
    ctx.rotate((compass.rotation * Math.PI) / 180);

    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.strokeStyle = '#232323';
    ctx.lineWidth = Math.max(1.5 / effectiveScale, 0.8);
    ctx.beginPath();
    ctx.arc(0, 0, size / 2, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();

    const directions = [
      { angle: 0, label: 'N', color: '#d32f2f' },
      { angle: 90, label: 'E', color: '#232323' },
      { angle: 180, label: 'S', color: '#232323' },
      { angle: 270, label: 'W', color: '#232323' },
    ];

    directions.forEach((dir) => {
      const angle = (dir.angle * Math.PI) / 180;
      const x = Math.sin(angle) * (size / 2 - 18);
      const y = -Math.cos(angle) * (size / 2 - 18);
      ctx.fillStyle = dir.color;
      ctx.font = `${Math.max(12 / effectiveScale, 7)}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(dir.label, x, y);
    });

    ctx.fillStyle = '#d32f2f';
    ctx.beginPath();
    ctx.moveTo(0, -(size / 2 - 6));
    ctx.lineTo(-7, -(size / 2 - 20));
    ctx.lineTo(0, -(size / 2 - 12));
    ctx.lineTo(7, -(size / 2 - 20));
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#ff6b35';
    ctx.strokeStyle = '#232323';
    ctx.lineWidth = Math.max(1.6 / effectiveScale, 0.8);
    ctx.beginPath();
    ctx.arc(0, -(size / 2 + 10), 6, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();

    // Resize handle (right side)
    ctx.fillStyle = '#2b6cf0';
    ctx.strokeStyle = '#232323';
    ctx.beginPath();
    ctx.arc(size / 2 + 10, 0, 6, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();

    ctx.restore();
  };

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#f6f6f6';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(viewport.panX, viewport.panY);
    ctx.scale(effectiveScale, effectiveScale);

    ctx.drawImage(image, 0, 0, image.width, image.height);
    drawGrid(ctx);

    const orderedRooms = [...rooms];
    if (selectedRoom) {
      const selectedIndex = orderedRooms.findIndex((room) => room.id === selectedRoom);
      if (selectedIndex >= 0) {
        const [selected] = orderedRooms.splice(selectedIndex, 1);
        orderedRooms.push(selected);
      }
    }

    const hasCarpetClip = Array.isArray(carpetPolygons) && carpetPolygons.some((poly) => Array.isArray(poly) && poly.length >= 3);
    if (hasCarpetClip) {
      ctx.save();
      ctx.beginPath();
      carpetPolygons.forEach((poly) => {
        if (!Array.isArray(poly) || poly.length < 3) return;
        ctx.moveTo(poly[0].x, poly[0].y);
        poly.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
        ctx.closePath();
      });
      ctx.clip();
    }

    orderedRooms.forEach((room) => {
      const isSelected = selectedRoom === room.id;
      ctx.fillStyle = getRoomColor(room.id, isSelected ? 0.46 : 0.20, isSelected ? 54 : 50);

      if (Array.isArray(room.polygon) && room.polygon.length >= 3) {
        ctx.beginPath();
        ctx.moveTo(room.polygon[0].x, room.polygon[0].y);
        room.polygon.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.fillRect(room.x, room.y, room.width, room.height);
      }

      ctx.strokeStyle = getRoomColor(room.id, isSelected ? 1 : 0.6, isSelected ? 34 : 42);
      ctx.lineWidth = Math.max((isSelected ? 3.2 : 1.4) / effectiveScale, 0.6);
      if (Array.isArray(room.polygon) && room.polygon.length >= 3) {
        ctx.beginPath();
        ctx.moveTo(room.polygon[0].x, room.polygon[0].y);
        room.polygon.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
        ctx.closePath();
        ctx.stroke();
      } else {
        ctx.strokeRect(room.x, room.y, room.width, room.height);
      }

      const bounds = getRoomBounds(room);
      const labelPoint = getPolygonCentroid(room.polygon, room);
      const label = getRoomDisplayLabel(room);
      const fontSize = Math.max(12 / effectiveScale, 7);
      ctx.font = `${fontSize}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const maxLabelWidth = Math.max(26, bounds.width - 8);
      const measuredWidth = ctx.measureText(label).width;
      const scaleRatio = measuredWidth > maxLabelWidth ? maxLabelWidth / measuredWidth : 1;
      const labelWidth = Math.min(measuredWidth, maxLabelWidth);
      const labelX = Math.max(bounds.x + labelWidth / 2 + 4, Math.min(bounds.x + bounds.width - labelWidth / 2 - 4, labelPoint.x));
      const labelY = Math.max(bounds.y + fontSize + 4, Math.min(bounds.y + bounds.height - fontSize - 4, labelPoint.y));

      ctx.save();
      ctx.translate(labelX, labelY);
      ctx.scale(scaleRatio, 1);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.82)';
      ctx.fillRect(-measuredWidth / 2 - 4, -fontSize / 2 - 3, measuredWidth + 8, fontSize + 6);
      ctx.strokeStyle = 'rgba(35, 35, 35, 0.18)';
      ctx.lineWidth = Math.max(1 / effectiveScale, 0.5);
      ctx.strokeRect(-measuredWidth / 2 - 4, -fontSize / 2 - 3, measuredWidth + 8, fontSize + 6);
      ctx.fillStyle = isSelected ? '#111111' : '#2b2b2b';
      ctx.fillText(label, 0, 0);
      ctx.restore();

      if (isSelected) {
        const handleSize = Math.max(8 / effectiveScale, 4.5);
        const handles = [
          { x: bounds.x, y: bounds.y },
          { x: bounds.x + bounds.width, y: bounds.y },
          { x: bounds.x, y: bounds.y + bounds.height },
          { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
          { x: bounds.x + bounds.width / 2, y: bounds.y },
          { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height },
          { x: bounds.x, y: bounds.y + bounds.height / 2 },
          { x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 },
        ];

        ctx.fillStyle = getRoomColor(room.id, 1, 46);
        handles.forEach((handle) => {
          ctx.fillRect(handle.x - handleSize / 2, handle.y - handleSize / 2, handleSize, handleSize);
        });
      }
    });

    if (hasCarpetClip) {
      ctx.restore();
    }

    if (showCompass) {
      drawCompass(ctx);
    }

    ctx.restore();
  }, [
    canvasSize.height,
    canvasSize.width,
    compass,
    effectiveScale,
    gridSize,
    image,
    getRoomColor,
    rooms,
    selectedRoom,
    showCompass,
    showGrid,
    viewport.panX,
    viewport.panY,
    carpetPolygons,
  ]);

  const getResizeHandle = useCallback(
    (room, mouseX, mouseY) => {
      const bounds = getRoomBounds(room);
      const handleSize = Math.max(8 / effectiveScale, 6);
      const handles = [
        { type: 'nw', x: bounds.x, y: bounds.y },
        { type: 'ne', x: bounds.x + bounds.width, y: bounds.y },
        { type: 'sw', x: bounds.x, y: bounds.y + bounds.height },
        { type: 'se', x: bounds.x + bounds.width, y: bounds.y + bounds.height },
        { type: 'n', x: bounds.x + bounds.width / 2, y: bounds.y },
        { type: 's', x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height },
        { type: 'w', x: bounds.x, y: bounds.y + bounds.height / 2 },
        { type: 'e', x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 },
      ];

      for (const handle of handles) {
        if (Math.abs(mouseX - handle.x) < handleSize && Math.abs(mouseY - handle.y) < handleSize) {
          return handle.type;
        }
      }
      return null;
    },
    [effectiveScale]
  );

  const isPointInCompass = (mouseX, mouseY) => {
    const dx = mouseX - compass.x;
    const dy = mouseY - compass.y;
    return Math.sqrt(dx * dx + dy * dy) <= compass.size / 2;
  };

  const isPointInCompassRotateHandle = (mouseX, mouseY) => {
    const angle = (compass.rotation * Math.PI) / 180;
    const handleX = compass.x + Math.sin(angle) * (compass.size / 2 + 10);
    const handleY = compass.y - Math.cos(angle) * (compass.size / 2 + 10);
    const dx = mouseX - handleX;
    const dy = mouseY - handleY;
    return Math.sqrt(dx * dx + dy * dy) <= Math.max(10 / effectiveScale, 6);
  };

  const isPointInCompassResizeHandle = (mouseX, mouseY) => {
    const angle = (compass.rotation * Math.PI) / 180;
    const handleX = compass.x + Math.cos(angle) * (compass.size / 2 + 10);
    const handleY = compass.y + Math.sin(angle) * (compass.size / 2 + 10);
    const dx = mouseX - handleX;
    const dy = mouseY - handleY;
    return Math.sqrt(dx * dx + dy * dy) <= Math.max(10 / effectiveScale, 6);
  };

  const findRoomAtPoint = useCallback(
    (point) => {
      for (let i = rooms.length - 1; i >= 0; i--) {
        const room = rooms[i];
        if (Array.isArray(room.polygon) && room.polygon.length >= 3) {
          if (pointInPolygon(point, room.polygon)) return room;
        } else if (
          point.x >= room.x &&
          point.x <= room.x + room.width &&
          point.y >= room.y &&
          point.y <= room.y + room.height
        ) {
          return room;
        }
      }
      return null;
    },
    [rooms]
  );

  const applyRoomUpdate = useCallback(
    (roomId, updater) => {
      setRooms((prevRooms) =>
        prevRooms.map((room) => {
          if (room.id !== roomId) return room;
          const updated = updater(room);
          return clampRoom(updated);
        })
      );
    },
    [clampRoom]
  );

  const handleCanvasMouseDown = (event) => {
    if (!image) return;
    if (event.button !== 0 && event.button !== 1) return;

    const point = getWorldPoint(event);
    const wantsPan = tool === 'pan' || event.button === 1 || event.shiftKey;

    if (wantsPan) {
      setDragging({
        type: 'pan',
        startScreenX: point.screenX,
        startScreenY: point.screenY,
        startPanX: viewport.panX,
        startPanY: viewport.panY,
      });
      return;
    }

    if (showCompass && isPointInCompassResizeHandle(point.x, point.y)) {
      setDragging({
        type: 'compass-resize',
        startDistance: Math.hypot(point.x - compass.x, point.y - compass.y),
        originalCompassSize: compass.size,
      });
      return;
    }

    if (showCompass && isPointInCompassRotateHandle(point.x, point.y)) {
      setCompassRotating(true);
      return;
    }

    if (showCompass && isPointInCompass(point.x, point.y)) {
      setDragging({
        type: 'compass',
        startX: point.x,
        startY: point.y,
        originalCompass: { ...compass },
      });
      return;
    }

    if (selectedRoom) {
      const room = rooms.find((r) => r.id === selectedRoom);
      if (room) {
        const handle = getResizeHandle(room, point.x, point.y);
        if (handle) {
          setDragging({
            type: 'resize-room',
            roomId: room.id,
            resizeType: handle,
            startX: point.x,
            startY: point.y,
            originalRoom: { ...room, polygon: room.polygon ? [...room.polygon] : undefined },
          });
          return;
        }
      }
    }

    const clickedRoom = findRoomAtPoint(point);
    if (clickedRoom) {
      setSelectedRoom(clickedRoom.id);
      setDragging({
        type: 'move-room',
        roomId: clickedRoom.id,
        startX: point.x,
        startY: point.y,
        originalRoom: { ...clickedRoom, polygon: clickedRoom.polygon ? [...clickedRoom.polygon] : undefined },
      });
    } else {
      setSelectedRoom(null);
    }
  };

  const handleCanvasMouseMove = (event) => {
    if (!image) return;

    const point = getWorldPoint(event);
    const canvas = canvasRef.current;

    if (compassRotating) {
      const dx = point.x - compass.x;
      const dy = point.y - compass.y;
      let angle = Math.atan2(dx, -dy) * (180 / Math.PI);
      if (angle < 0) angle += 360;
      setCompass((prev) => ({ ...prev, rotation: angle }));
      if (canvas) canvas.style.cursor = 'grabbing';
      return;
    }

    if (!dragging) {
      if (canvas) {
        if (tool === 'pan') {
          canvas.style.cursor = 'grab';
        } else if (showCompass && isPointInCompassResizeHandle(point.x, point.y)) {
          canvas.style.cursor = 'ew-resize';
        } else if (showCompass && isPointInCompassRotateHandle(point.x, point.y)) {
          canvas.style.cursor = 'grab';
        } else if (showCompass && isPointInCompass(point.x, point.y)) {
          canvas.style.cursor = 'move';
        } else {
          const hoveredRoom = selectedRoom ? rooms.find((room) => room.id === selectedRoom) : null;
          const handle = hoveredRoom ? getResizeHandle(hoveredRoom, point.x, point.y) : null;
          if (handle) {
            const cursors = {
              nw: 'nw-resize',
              ne: 'ne-resize',
              sw: 'sw-resize',
              se: 'se-resize',
              n: 'n-resize',
              s: 's-resize',
              w: 'w-resize',
              e: 'e-resize',
            };
            canvas.style.cursor = cursors[handle] || 'default';
          } else {
            canvas.style.cursor = 'crosshair';
          }
        }
      }
      return;
    }

    if (dragging.type === 'pan') {
      setViewport((prev) => ({
        ...prev,
        panX: dragging.startPanX + (point.screenX - dragging.startScreenX),
        panY: dragging.startPanY + (point.screenY - dragging.startScreenY),
      }));
      userAdjustedViewRef.current = true;
      return;
    }

    if (dragging.type === 'compass') {
      setCompass((prev) => ({
        ...prev,
        x: dragging.originalCompass.x + (point.x - dragging.startX),
        y: dragging.originalCompass.y + (point.y - dragging.startY),
      }));
      return;
    }

    if (dragging.type === 'compass-resize') {
      const currentDistance = Math.hypot(point.x - compass.x, point.y - compass.y);
      const nextSize = dragging.originalCompassSize + (currentDistance - dragging.startDistance) * 2;
      setCompass((prev) => ({
        ...prev,
        size: Math.max(28, Math.min(260, nextSize)),
      }));
      if (canvas) canvas.style.cursor = 'ew-resize';
      return;
    }

    if (dragging.type === 'move-room') {
      const deltaX = point.x - dragging.startX;
      const deltaY = point.y - dragging.startY;
      applyRoomUpdate(dragging.roomId, (room) => {
        const nextRoom = {
          ...room,
          x: snapValue(dragging.originalRoom.x + deltaX),
          y: snapValue(dragging.originalRoom.y + deltaY),
        };

        if (Array.isArray(dragging.originalRoom.polygon)) {
          nextRoom.polygon = dragging.originalRoom.polygon.map((p) => ({
            x: snapValue(p.x + deltaX),
            y: snapValue(p.y + deltaY),
          }));
        }
        return nextRoom;
      });
      return;
    }

    if (dragging.type === 'resize-room') {
      const deltaX = point.x - dragging.startX;
      const deltaY = point.y - dragging.startY;
      applyRoomUpdate(dragging.roomId, () => {
        const orig = getRoomBounds(dragging.originalRoom);
        const nextRoom = {
          ...dragging.originalRoom,
          polygon: undefined,
          x: orig.x,
          y: orig.y,
          width: orig.width,
          height: orig.height,
        };

        switch (dragging.resizeType) {
          case 'se':
            nextRoom.width = Math.max(MIN_ROOM_SIZE, orig.width + deltaX);
            nextRoom.height = Math.max(MIN_ROOM_SIZE, orig.height + deltaY);
            break;
          case 'sw':
            nextRoom.x = orig.x + deltaX;
            nextRoom.width = Math.max(MIN_ROOM_SIZE, orig.width - deltaX);
            nextRoom.height = Math.max(MIN_ROOM_SIZE, orig.height + deltaY);
            break;
          case 'ne':
            nextRoom.y = orig.y + deltaY;
            nextRoom.width = Math.max(MIN_ROOM_SIZE, orig.width + deltaX);
            nextRoom.height = Math.max(MIN_ROOM_SIZE, orig.height - deltaY);
            break;
          case 'nw':
            nextRoom.x = orig.x + deltaX;
            nextRoom.y = orig.y + deltaY;
            nextRoom.width = Math.max(MIN_ROOM_SIZE, orig.width - deltaX);
            nextRoom.height = Math.max(MIN_ROOM_SIZE, orig.height - deltaY);
            break;
          case 'e':
            nextRoom.width = Math.max(MIN_ROOM_SIZE, orig.width + deltaX);
            break;
          case 'w':
            nextRoom.x = orig.x + deltaX;
            nextRoom.width = Math.max(MIN_ROOM_SIZE, orig.width - deltaX);
            break;
          case 's':
            nextRoom.height = Math.max(MIN_ROOM_SIZE, orig.height + deltaY);
            break;
          case 'n':
            nextRoom.y = orig.y + deltaY;
            nextRoom.height = Math.max(MIN_ROOM_SIZE, orig.height - deltaY);
            break;
          default:
            break;
        }

        nextRoom.x = snapValue(nextRoom.x);
        nextRoom.y = snapValue(nextRoom.y);
        nextRoom.width = Math.max(MIN_ROOM_SIZE, snapValue(nextRoom.width));
        nextRoom.height = Math.max(MIN_ROOM_SIZE, snapValue(nextRoom.height));
        return nextRoom;
      });
    }
  };

  const handleCanvasMouseUp = () => {
    setDragging(null);
    setCompassRotating(false);
  };

  const handleWheel = useCallback(
    (event) => {
      if (!image || !canvasRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      const rect = canvasRef.current.getBoundingClientRect();
      const screenX = event.clientX - rect.left;
      const screenY = event.clientY - rect.top;
      const zoomFactor = event.deltaY < 0 ? 1.1 : 0.9;
      zoomAtPoint(screenX, screenY, viewport.zoom * zoomFactor);
    },
    [image, viewport.zoom, zoomAtPoint]
  );

  const addRoom = () => {
    if (!image) return;
    const roomWidth = 180;
    const roomHeight = 140;
    const worldCenterX = (canvasSize.width / 2 - viewport.panX) / (effectiveScale || 1);
    const worldCenterY = (canvasSize.height / 2 - viewport.panY) / (effectiveScale || 1);
    const newRoom = clampRoom({
      id: Date.now(),
      type: 'Living Room',
      x: snapValue(worldCenterX - roomWidth / 2),
      y: snapValue(worldCenterY - roomHeight / 2),
      width: roomWidth,
      height: roomHeight,
    });
    setRooms((prev) => [...prev, newRoom]);
    setSelectedRoom(newRoom.id);
  };

  const duplicateSelectedRoom = () => {
    const room = rooms.find((r) => r.id === selectedRoom);
    if (!room) return;
    const duplicate = clampRoom({
      ...room,
      id: Date.now(),
      x: room.x + 20,
      y: room.y + 20,
      polygon: Array.isArray(room.polygon)
        ? room.polygon.map((p) => ({ x: p.x + 20, y: p.y + 20 }))
        : undefined,
    });
    setRooms((prev) => [...prev, duplicate]);
    setSelectedRoom(duplicate.id);
  };

  const bringSelectedRoomToFront = () => {
    if (!selectedRoom) return;
    setRooms((prevRooms) => {
      const idx = prevRooms.findIndex((room) => room.id === selectedRoom);
      if (idx < 0 || idx === prevRooms.length - 1) return prevRooms;
      const reordered = [...prevRooms];
      const [room] = reordered.splice(idx, 1);
      reordered.push(room);
      return reordered;
    });
  };

  const deleteRoom = (id) => {
    setRooms((prev) => prev.filter((room) => room.id !== id));
    if (selectedRoom === id) setSelectedRoom(null);
  };

  const updateRoomType = (id, newType) => {
    setRooms((prev) => prev.map((room) => (room.id === id ? { ...room, type: newType } : room)));
    setEditingRoom(null);
  };

  const updateRoomSize = (id, field, value) => {
    const numValue = Number(value);
    if (!Number.isFinite(numValue) || numValue < MIN_ROOM_SIZE) return;
    setRooms((prev) =>
      prev.map((room) => {
        if (room.id !== id) return room;
        const next = { ...room, [field]: numValue, polygon: undefined };
        return clampRoom(next);
      })
    );
  };

  const rotateCompass = (degrees) => {
    setCompass((prev) => {
      let nextRotation = prev.rotation + degrees;
      while (nextRotation < 0) nextRotation += 360;
      while (nextRotation >= 360) nextRotation -= 360;
      return { ...prev, rotation: nextRotation };
    });
  };

  const resetCompassRotation = () => {
    setCompass((prev) => ({ ...prev, rotation: 0 }));
  };

  const handleAnalyze = async () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'CONFIRM_ANALYZE',
        payload: {
          property_type: propertyType,
          orientation,
          floor_plan_base64: floorPlanBase64,
          detected_rooms: rooms,
          compass_rotation: compass.rotation,
        }
      }));
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${API}/vastu/upload`,
        {
          property_type: propertyType,
          orientation,
          floor_plan_base64: floorPlanBase64,
          detected_rooms: rooms,
          compass_rotation: compass.rotation,
        },
        { withCredentials: true }
      );
      navigate(`/report/${response.data.id}`);
    } catch (error) {
      console.error('Analysis error:', error);
      alert('Failed to analyze floor plan. Please try again.');
      setAnalyzing(false);
    }
  };

  const handleRetryDetection = async () => {
    if (!floorPlanBase64 || !image) return;
    if (!detectionFailed && rooms.length > 0) {
      const shouldReplace = window.confirm('Retry detection will replace the current room overlays. Continue?');
      if (!shouldReplace) return;
    }

    setRetryingDetection(true);
    setDetectionErrors([]);
    setSelectedRoom(null);
    setEditingRoom(null);
    setEditingSize(null);

    try {
      const response = await axios.post(
        `${API}/vastu/detect-rooms`,
        {
          floor_plan_base64: floorPlanBase64,
          property_type: propertyType || 'home',
          orientation: orientation || 'north',
        },
        { withCredentials: true }
      );

      const detectionPayload = response?.data?.rooms || {};
      const nextDetectedRooms = Array.isArray(detectionPayload.rooms) ? detectionPayload.rooms : [];
      const nextErrors = Array.isArray(detectionPayload.errors) ? detectionPayload.errors : [];
      const nextStatus =
        detectionPayload?.detection_status ||
        (nextDetectedRooms.length > 0 ? 'success' : nextErrors.length > 0 ? 'failed' : 'unknown');

      setDetectionStatus(nextStatus);
      setDetectionErrors(nextErrors);
      setDetectionMetaState(detectionPayload);

      const { convertedRooms, convertedCarpet } = toCanvasDetections(nextDetectedRooms, detectionPayload, image);
      if (nextStatus === 'success') {
        setRooms(convertedRooms);
        setCarpetPolygons(convertedCarpet);
      } else if (nextDetectedRooms.length === 0) {
        setRooms([]);
        setCarpetPolygons([]);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setDetectionStatus('failed');
      setDetectionErrors([detail || 'Retry failed. Please try again.']);
    } finally {
      setRetryingDetection(false);
    }
  };

  useEffect(() => {
    setDetectionMetaState(detectionMeta || {});
    setDetectionStatus(
      detectionMeta?.detection_status || (Array.isArray(detectedRooms) && detectedRooms.length > 0 ? 'success' : 'unknown')
    );
    setDetectionErrors(Array.isArray(detectionMeta?.errors) ? detectionMeta.errors : []);
  }, [detectedRooms, detectionMeta]);

  useEffect(() => {
    if (!floorPlanBase64) {
      navigate('/upload');
      return;
    }

    const img = new Image();
    img.onload = () => {
      setImage(img);
      setImageLoaded(true);

      if (Array.isArray(detectedRooms) && detectedRooms.length > 0) {
        const { convertedRooms, convertedCarpet } = toCanvasDetections(detectedRooms, detectionMeta, img);
        setRooms(convertedRooms);
        setCarpetPolygons(convertedCarpet);
      } else {
        setRooms([]);
        setCarpetPolygons([]);
      }
    };

    img.onerror = () => {
      alert('Failed to load floor plan image. Please try uploading again.');
      navigate('/upload');
    };

    img.src = `data:image/png;base64,${floorPlanBase64}`;
  }, [detectedRooms, detectionMeta, floorPlanBase64, navigate, toCanvasDetections]);

  useEffect(() => {
    const updateCanvasSize = () => {
      const wrapper = canvasWrapperRef.current;
      if (!wrapper) return;
      const nextWidth = Math.max(520, Math.floor(wrapper.clientWidth));
      const targetHeight = Math.floor(window.innerHeight * (focusMode ? 0.82 : 0.72));
      const nextHeight = Math.max(420, targetHeight);
      setCanvasSize({ width: nextWidth, height: nextHeight });
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [focusMode]);

  useEffect(() => {
    if (focusMode) {
      document.body.classList.add('editor-focus-mode');
    } else {
      document.body.classList.remove('editor-focus-mode');
    }
    return () => {
      document.body.classList.remove('editor-focus-mode');
    };
  }, [focusMode]);

  useEffect(() => {
    if (!image) return;
    if (!userAdjustedViewRef.current) {
      fitToView();
    }
  }, [canvasSize, fitToView, image]);

  useEffect(() => {
    if (imageLoaded && image) {
      drawCanvas();
    }
  }, [drawCanvas, image, imageLoaded, rooms, viewport, compass, showGrid, gridSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wheelListener = (event) => handleWheel(event);
    canvas.addEventListener('wheel', wheelListener, { passive: false });
    return () => canvas.removeEventListener('wheel', wheelListener);
  }, [handleWheel, imageLoaded]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!image) return;

      if (event.key === 'Delete' && selectedRoom) {
        deleteRoom(selectedRoom);
      }
      if (event.key.toLowerCase() === 'g') {
        setShowGrid((prev) => !prev);
      }
      if (event.key.toLowerCase() === 'f') {
        fitToView();
      }
      if (event.key === '+' || event.key === '=') {
        zoomAtPoint(canvasSize.width / 2, canvasSize.height / 2, viewport.zoom * 1.1);
      }
      if (event.key === '-') {
        zoomAtPoint(canvasSize.width / 2, canvasSize.height / 2, viewport.zoom * 0.9);
      }
      if (event.key === '0') {
        fitToView();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canvasSize.height, canvasSize.width, fitToView, image, selectedRoom, viewport.zoom, zoomAtPoint]);

  if (!floorPlanBase64) return null;

  const selectedRoomData = rooms.find((room) => room.id === selectedRoom);
  const totalArea = rooms.reduce((sum, room) => sum + room.width * room.height, 0);

  return (
    <div className={focusMode ? 'fixed inset-0 z-[1200] bg-[#f6f6f6] overflow-auto p-4' : 'main-content min-h-screen py-8 px-4'}>
      <div className="max-w-[1800px] mx-auto">
        <div className="mb-6">
          <h1 className="text-4xl font-bold mb-2 text-[#232323]">Floor Plan Analyzer and Builder</h1>
          <p className="text-[#353535]">
            Full canvas editor with zoom, pan, grid, snapping, compass orientation, and AI-detected room refinement.
          </p>
          <div className="mt-3 p-4 bg-[var(--accent-saffron)] rounded-lg border-2 border-[var(--vedic-saffron)]">
            <p className="text-sm text-[var(--text-primary)] font-medium mb-2">
              <strong>Power tools:</strong> Mouse wheel zoom locks page scroll inside canvas, Shift+drag or Pan mode moves canvas, sticky eye icon toggles compass, G toggles grid, F/0 fit-to-screen.
            </p>
            {detectionMetaState?.detected_features?.floor_plan_style && 
             detectionMetaState.detected_features.floor_plan_style !== 'architectural' && (
              <div className="mt-2 p-3 bg-amber-50 rounded border border-amber-300 text-amber-800 text-xs font-medium flex items-start gap-2">
                <span className="text-sm">⚠️</span>
                <span>
                  <strong>Warning:</strong> The room detection for this floorplan may not be accurate because it was classified as a <strong>{detectionMetaState.detected_features.floor_plan_style}</strong>. For best results, please upload clean 2D architectural drawings.
                </span>
              </div>
            )}
            {detectionFailed && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <p className="text-sm text-red-700 font-medium">
                  Detection failed: {detectionErrors[0] || 'Unable to detect rooms from the provided image.'}
                </p>
                <button
                  onClick={handleRetryDetection}
                  disabled={retryingDetection}
                  className="px-2 py-1 rounded border border-red-300 bg-white text-red-700 text-xs font-medium inline-flex items-center gap-1 disabled:opacity-60"
                >
                  {retryingDetection ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Retry detection
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-semibold text-[#232323]">Floor Plan Canvas</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setTool('select')}
                    className={`px-2 py-1.5 rounded border text-xs font-medium normal-case tracking-normal flex items-center gap-1 ${
                      tool === 'select' ? 'bg-[#232323] text-white border-[#232323]' : 'bg-white border-[#999999] text-[#232323] hover:bg-[#f8f8f8]'
                    }`}
                    title="Select tool"
                  >
                    <MousePointer2 className="w-4 h-4" /> Select
                  </button>
                  <button
                    onClick={() => setTool('pan')}
                    className={`px-2 py-1.5 rounded border text-xs font-medium normal-case tracking-normal flex items-center gap-1 ${
                      tool === 'pan' ? 'bg-[#232323] text-white border-[#232323]' : 'bg-white border-[#999999] text-[#232323] hover:bg-[#f8f8f8]'
                    }`}
                    title="Pan tool"
                  >
                    <Move className="w-4 h-4" /> Pan
                  </button>
                  <button
                    onClick={addRoom}
                    className="px-2 py-1.5 rounded border border-[#999999] bg-white text-[#232323] hover:bg-[#f8f8f8] text-xs font-medium normal-case tracking-normal inline-flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add Room
                  </button>
                  <button
                    onClick={handleRetryDetection}
                    disabled={retryingDetection || !imageLoaded}
                    className="px-2 py-1.5 rounded border border-[#999999] bg-white text-[#232323] hover:bg-[#f8f8f8] text-xs font-medium normal-case tracking-normal inline-flex items-center gap-1 disabled:opacity-50"
                    title="Run room detection again on this same floor plan"
                  >
                    {retryingDetection ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                    Retry AI
                  </button>
                  <button
                    onClick={duplicateSelectedRoom}
                    disabled={!selectedRoom}
                    className="px-2 py-1.5 rounded border border-[#999999] bg-white text-[#232323] hover:bg-[#f8f8f8] text-xs font-medium normal-case tracking-normal inline-flex items-center justify-center disabled:opacity-50"
                    title="Duplicate selected room"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={bringSelectedRoomToFront}
                    disabled={!selectedRoom}
                    className="px-2 py-1.5 rounded border border-[#999999] bg-white text-[#232323] hover:bg-[#f8f8f8] text-xs font-medium normal-case tracking-normal inline-flex items-center justify-center disabled:opacity-50"
                    title="Bring selected room to front"
                  >
                    <MoveUpRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShow3DPreview(true)}
                    className="px-2 py-1.5 rounded border border-[#999999] bg-white text-[#232323] hover:bg-[#f8f8f8] text-xs font-medium normal-case tracking-normal inline-flex items-center gap-1"
                  >
                    <Cuboid className="w-4 h-4" /> 3D
                  </button>
                  <button
                    onClick={() => setFocusMode((prev) => !prev)}
                    className="px-2 py-1.5 rounded border border-[#999999] bg-white text-[#232323] hover:bg-[#f8f8f8] text-xs font-medium normal-case tracking-normal inline-flex items-center justify-center"
                    title={focusMode ? 'Exit focus mode' : 'Focus mode'}
                  >
                    {focusMode ? <ScanLine className="w-4 h-4" /> : <Scan className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="mb-3 flex flex-wrap gap-2 items-center">
                <button
                  onClick={() => zoomAtPoint(canvasSize.width / 2, canvasSize.height / 2, viewport.zoom * 1.1)}
                  className="px-2 py-1.5 rounded border border-[#999999] bg-white text-[#232323] hover:bg-[#f8f8f8] inline-flex items-center justify-center"
                  title="Zoom in"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={() => zoomAtPoint(canvasSize.width / 2, canvasSize.height / 2, viewport.zoom * 0.9)}
                  className="px-2 py-1.5 rounded border border-[#999999] bg-white text-[#232323] hover:bg-[#f8f8f8] inline-flex items-center justify-center"
                  title="Zoom out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  onClick={fitToView}
                  className="px-2 py-1.5 rounded border border-[#999999] bg-white text-[#232323] hover:bg-[#f8f8f8] inline-flex items-center justify-center"
                  title="Fit to view"
                >
                  <Maximize className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowGrid((prev) => !prev)}
                  className={`px-2 py-1.5 rounded border border-[#999999] text-[#232323] inline-flex items-center justify-center ${showGrid ? 'bg-[#efefef]' : 'bg-white hover:bg-[#f8f8f8]'}`}
                  title={showGrid ? 'Hide grid' : 'Show grid'}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setSnapToGrid((prev) => !prev)}
                  className={`px-2 py-1.5 rounded border border-[#999999] text-[#232323] inline-flex items-center justify-center ${snapToGrid ? 'bg-[#efefef]' : 'bg-white hover:bg-[#f8f8f8]'}`}
                  title={snapToGrid ? 'Disable snap' : 'Enable snap'}
                >
                  <Magnet className="w-4 h-4" />
                </button>
                <label className="text-xs text-[#353535] flex items-center gap-2">
                  Grid
                  <input
                    type="number"
                    min="10"
                    max="200"
                    step="5"
                    value={gridSize}
                    onChange={(e) => setGridSize(Math.max(10, Math.min(200, Number(e.target.value) || 40)))}
                    className="w-16 px-2 py-1 border border-[#999999] rounded"
                  />
                  px
                </label>
                <span className="text-xs text-[#353535] ml-auto">Zoom: {(viewport.zoom * 100).toFixed(0)}%</span>
              </div>

              {!imageLoaded ? (
                <div className="border-2 border-[#999999] rounded-lg p-20 text-center">
                  <div className="animate-spin w-12 h-12 border-4 border-[#232323] border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-[#353535]">Loading floor plan...</p>
                </div>
              ) : (
                <div ref={canvasWrapperRef} className="relative w-full border-2 border-[#999999] rounded-lg overflow-hidden">
                  {retryingDetection && (
                    <div className="absolute inset-0 z-20 bg-white/70 backdrop-blur-[1px] flex items-center justify-center">
                      <div className="px-4 py-3 rounded-lg border border-[#999999] bg-white shadow-sm flex items-center gap-2 text-sm font-medium text-[#232323]">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Retrying room detection...
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setShowCompass((prev) => !prev)}
                    className="absolute top-3 right-3 z-10 bg-white/95 border border-[#999999] rounded-full p-2 shadow-sm hover:bg-white"
                    title={showCompass ? 'Hide compass' : 'Show compass'}
                  >
                    {showCompass ? <EyeOff className="w-4 h-4 text-[#232323]" /> : <Eye className="w-4 h-4 text-[#232323]" />}
                  </button>
                  <canvas
                    ref={canvasRef}
                    onMouseDown={handleCanvasMouseDown}
                    onMouseMove={handleCanvasMouseMove}
                    onMouseUp={handleCanvasMouseUp}
                    onMouseLeave={handleCanvasMouseUp}
                    className="w-full touch-none"
                    style={{ display: 'block' }}
                  />
                </div>
              )}

              <p className="text-sm text-[#999999] mt-3">
                <strong>Canvas:</strong> Full-width responsive viewport with zoom and pan. Shift + drag to pan anytime. Delete key removes selected room.
              </p>
            </div>
          </div>

          <div>
            <div className="bg-white rounded-xl p-6 shadow-sm sticky top-24">
              <div className="mb-4">
                <h3 className="font-semibold text-[#232323]">Project Stats</h3>
                <p className="text-sm text-[#353535]">{rooms.length} room{rooms.length !== 1 ? 's' : ''} identified</p>
                <p className="text-sm text-[#353535]">Total area (pixel estimate): {Math.round(totalArea).toLocaleString()}</p>
              </div>

              <div className="mb-6 p-4 bg-[var(--accent-saffron)] rounded-lg border-2 border-[var(--vedic-saffron)]">
                <h3 className="font-semibold text-[#232323] mb-2 flex items-center gap-2">
                  North Direction
                  <button
                    onClick={() => setShowCompass((prev) => !prev)}
                    className="ml-auto px-2 py-1 rounded border border-[#999999] bg-white text-xs"
                  >
                    {showCompass ? 'Hide' : 'Show'}
                  </button>
                </h3>
                <div className="bg-white rounded p-2 mb-3 text-center">
                  <p className="text-2xl font-bold text-[#232323]">{compass.rotation.toFixed(1)} deg</p>
                  <p className="text-xs text-[#353535]">Current rotation</p>
                </div>
                <div className="bg-white rounded p-2 mb-3">
                  <div className="flex items-center justify-between text-xs text-[#353535] mb-1">
                    <span>Compass size</span>
                    <span>{Math.round(compass.size)} px</span>
                  </div>
                  <input
                    type="range"
                    min="28"
                    max="260"
                    step="1"
                    value={compass.size}
                    disabled={!showCompass}
                    onChange={(e) =>
                      setCompass((prev) => ({
                        ...prev,
                        size: Math.max(28, Math.min(260, Number(e.target.value) || prev.size)),
                      }))
                    }
                    className="w-full"
                  />
                  <p className="text-[11px] text-[#666666] mt-1">Blue handle on compass can also resize directly on canvas.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <button
                      onClick={() => rotateCompass(-15)}
                      disabled={!showCompass}
                      className="flex-1 px-3 py-2 bg-white border-2 border-[#232323] text-[#232323] rounded text-sm font-medium disabled:opacity-50"
                    >
                      Left 15 deg
                    </button>
                    <button
                      onClick={() => rotateCompass(15)}
                      disabled={!showCompass}
                      className="flex-1 px-3 py-2 bg-white border-2 border-[#232323] text-[#232323] rounded text-sm font-medium disabled:opacity-50"
                    >
                      Right 15 deg
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => rotateCompass(-1)}
                      disabled={!showCompass}
                      className="flex-1 px-3 py-1 bg-white border border-[#999999] text-[#232323] rounded text-xs disabled:opacity-50"
                    >
                      Left 1 deg
                    </button>
                    <button
                      onClick={() => rotateCompass(1)}
                      disabled={!showCompass}
                      className="flex-1 px-3 py-1 bg-white border border-[#999999] text-[#232323] rounded text-xs disabled:opacity-50"
                    >
                      Right 1 deg
                    </button>
                  </div>
                  <button
                    onClick={resetCompassRotation}
                    disabled={!showCompass}
                    className="w-full px-3 py-2 bg-[#232323] text-white rounded text-sm font-medium disabled:opacity-50"
                  >
                    Reset to 0 deg
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <h3 className="font-semibold text-[#232323] flex items-center gap-2">
                  <Layers className="w-4 h-4" /> Detected Rooms
                </h3>
                {selectedRoomData && (
                  <p className="text-xs text-[#353535] mt-1">
                    Selected: {selectedRoomData.type} ({Math.round(selectedRoomData.width)} x {Math.round(selectedRoomData.height)} px)
                  </p>
                )}
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto mb-6">
                {rooms.map((room) => {
                  const isSelected = selectedRoom === room.id;
                  const colorStrong = getRoomColor(room.id, 1, 42);
                  const colorSoft = getRoomColor(room.id, isSelected ? 0.16 : 0.07, 54);
                  return (
                  <div
                    key={room.id}
                    className="p-3 rounded-lg border-2 transition-all cursor-pointer"
                    style={{
                      borderColor: isSelected ? colorStrong : 'rgba(153,153,153,0.9)',
                      background: colorSoft,
                    }}
                    onClick={() => setSelectedRoom(room.id)}
                  >
                    {editingRoom === room.id ? (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={room.type}
                          onChange={(e) => updateRoomType(room.id, e.target.value)}
                          className="w-full px-2 py-1 border-2 border-[#999999] rounded text-sm"
                          autoFocus
                        >
                          {roomTypes.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => setEditingRoom(null)}
                          className="w-full px-2 py-1 bg-[#232323] text-white rounded text-sm flex items-center justify-center gap-1"
                        >
                          <Check className="w-3 h-3" /> Done
                        </button>
                      </div>
                    ) : editingSize === room.id ? (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <div className="text-xs font-medium text-[#232323] mb-1">{room.type}</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-[#353535]">Width</label>
                            <input
                              type="number"
                              value={Math.round(room.width)}
                              onChange={(e) => updateRoomSize(room.id, 'width', e.target.value)}
                              className="w-full px-2 py-1 border border-[#999999] rounded text-sm"
                              min={MIN_ROOM_SIZE}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-[#353535]">Height</label>
                            <input
                              type="number"
                              value={Math.round(room.height)}
                              onChange={(e) => updateRoomSize(room.id, 'height', e.target.value)}
                              className="w-full px-2 py-1 border border-[#999999] rounded text-sm"
                              min={MIN_ROOM_SIZE}
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => setEditingSize(null)}
                          className="w-full px-2 py-1 bg-[#232323] text-white rounded text-sm flex items-center justify-center gap-1"
                        >
                          <Check className="w-3 h-3" /> Done
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm text-[#232323] flex items-center gap-2">
                            <span
                              className="inline-block w-3 h-3 rounded-full border border-black/20"
                              style={{ background: getRoomColor(room.id, 0.95, 48) }}
                            />
                            {room.type}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingRoom(room.id);
                                setEditingSize(null);
                              }}
                              className="p-1 hover:bg-white rounded"
                              title="Edit type"
                            >
                              <Edit2 className="w-4 h-4 text-[#353535]" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteRoom(room.id);
                              }}
                              className="p-1 hover:bg-red-100 rounded"
                              title="Delete room"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-[#353535]">
                          <span>{Math.round(room.width)} x {Math.round(room.height)} px</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingSize(room.id);
                              setEditingRoom(null);
                            }}
                            className="text-[#FF6B35] hover:underline font-medium"
                          >
                            Resize
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )})}
              </div>

              {analyzing ? (
                <button disabled className="btn-primary w-full py-3 flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" /> Analyzing...
                </button>
              ) : (
                <button onClick={handleAnalyze} className="btn-primary w-full py-3 flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" /> Confirm and Analyze
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <FloorPlan3DPreviewModal
        open={show3DPreview}
        onClose={() => setShow3DPreview(false)}
        rooms={rooms}
        planWidth={image?.width || 1}
        planHeight={image?.height || 1}
      />
    </div>
  );
};

const FloorPlan3DPreviewModal = ({ open, onClose, rooms, planWidth, planHeight }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    if (!open || !mountRef.current) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f2ec);

    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 200);
    camera.position.set(16, 13, 16);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mountRef.current.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.maxPolarAngle = Math.PI * 0.49;
    controls.minDistance = 4;
    controls.maxDistance = 60;

    const ambient = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambient);

    const dir = new THREE.DirectionalLight(0xfff3df, 1.0);
    dir.position.set(12, 18, 10);
    scene.add(dir);

    const worldWidth = 14;
    const worldDepth = worldWidth * (planHeight / Math.max(planWidth, 1));
    const scaleX = worldWidth / Math.max(planWidth, 1);
    const scaleZ = worldDepth / Math.max(planHeight, 1);

    const toWorldPoint = (px, py) => {
      const x = (px - planWidth / 2) * scaleX;
      const z = (py - planHeight / 2) * scaleZ;
      return { x, z: -z };
    };

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(worldWidth + 4, worldDepth + 4),
      new THREE.MeshStandardMaterial({ color: 0xe9e1d3, roughness: 0.9 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.02;
    scene.add(floor);

    const planBase = new THREE.Mesh(
      new THREE.PlaneGeometry(worldWidth, worldDepth),
      new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.95 })
    );
    planBase.rotation.x = -Math.PI / 2;
    scene.add(planBase);

    const grid = new THREE.GridHelper(Math.max(worldWidth, worldDepth), 28, 0xb8b8b8, 0xd9d9d9);
    grid.position.y = 0.001;
    scene.add(grid);

    const makeRoomShape = (room) => {
      let points = [];
      if (Array.isArray(room.polygon) && room.polygon.length >= 3) {
        points = room.polygon.map((p) => toWorldPoint(p.x, p.y));
      } else {
        points = [
          toWorldPoint(room.x, room.y),
          toWorldPoint(room.x + room.width, room.y),
          toWorldPoint(room.x + room.width, room.y + room.height),
          toWorldPoint(room.x, room.y + room.height),
        ];
      }

      const shape = new THREE.Shape();
      shape.moveTo(points[0].x, points[0].z);
      for (let i = 1; i < points.length; i++) {
        shape.lineTo(points[i].x, points[i].z);
      }
      shape.closePath();
      return shape;
    };

    const roomGroup = new THREE.Group();
    const sortedRooms = [...rooms].sort((a, b) => (b.width * b.height) - (a.width * a.height));
    sortedRooms.forEach((room, idx) => {
      const shape = makeRoomShape(room);
      const height = 0.65 + (Math.max(0, Math.min(1, room.confidence || 0.55)) * 1.1);
      const geometry = new THREE.ExtrudeGeometry(shape, {
        depth: height,
        bevelEnabled: false,
      });
      geometry.rotateX(-Math.PI / 2);

      const hue = ((idx * 41) + 210) % 360;
      const color = new THREE.Color(`hsl(${hue}, 70%, 58%)`);
      const material = new THREE.MeshStandardMaterial({
        color,
        transparent: true,
        opacity: 0.82,
        roughness: 0.42,
        metalness: 0.06,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.y = 0.02;
      roomGroup.add(mesh);

      const edges = new THREE.EdgesGeometry(geometry);
      const edgeMesh = new THREE.LineSegments(
        edges,
        new THREE.LineBasicMaterial({ color: 0x242424, transparent: true, opacity: 0.6 })
      );
      edgeMesh.position.copy(mesh.position);
      roomGroup.add(edgeMesh);
    });
    scene.add(roomGroup);

    controls.target.set(0, 0.4, 0);
    controls.update();

    let rafId = null;
    const resize = () => {
      if (!mountRef.current) return;
      const w = mountRef.current.clientWidth;
      const h = mountRef.current.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();

    const animate = () => {
      rafId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      if (rafId) cancelAnimationFrame(rafId);
      controls.dispose();
      renderer.dispose();
      if (mountRef.current && renderer.domElement.parentNode === mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [open, planHeight, planWidth, rooms]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1600] bg-black/65 backdrop-blur-[2px] flex items-center justify-center p-4">
      <div className="w-full max-w-6xl h-[86vh] bg-white rounded-xl border border-[#d6d6d6] overflow-hidden shadow-2xl">
        <div className="h-14 px-4 border-b border-[#e3e3e3] flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-[#232323]">3D Floor Plan Preview</h3>
            <p className="text-xs text-[#666666]">Polygon-based extrusion from detected room geometry</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full border border-[#bdbdbd] hover:bg-[#f4f4f4]">
            <X className="w-4 h-4 text-[#232323]" />
          </button>
        </div>
        <div ref={mountRef} className="w-full h-[calc(86vh-56px)]" />
      </div>
    </div>
  );
};

export default FloorPlanEditor;
